/**
 * Um bloco do documento na mesa (E202.F1, GUS-145, ADR-0145 §1): a faixa de abertura, o título
 * numerado da seção, o título de bloco e de subseção, o texto corrido (parágrafo, nota, itens e
 * caixa) e as tabelas. O texto de redação traz a marca discreta "Editado" (ou "Editado na análise
 * N", quando a edição veio de uma análise anterior) e o trecho tirado fica no lugar, sem texto.
 * E202.F2: o texto de redação abre o editor no clique (`ParteDoDocumento`). E203.F2 (ADR-0146 §1):
 * a tabela, o quadro e o texto protegido abrem o painel do dado pela origem do bloco.
 */
import { ptBR } from '@/i18n/pt-BR';
import type { BlocoDoDocumentoData } from '@/lib/documentoDaSaida';

import { GradeDoDocumento } from './GradeDoDocumento';
import { MapaDoRisco } from './MapaDoRisco';
import { PALETA } from './paleta';
import { ParteDoDocumento, type ContextoDasPartes } from './ParteDoDocumento';

export { marcaDaParte, type ContextoDasPartes } from './ParteDoDocumento';

const t = ptBR.documentoDaMesa;

function Partes({
  bloco,
  contexto,
  secao = null,
}: {
  bloco: BlocoDoDocumentoData;
  contexto: ContextoDasPartes;
  secao?: string | null;
}) {
  return (
    <>
      {bloco.partes.map((parte, i) => (
        <ParteDoDocumento
          key={parte.trecho_id ?? i}
          parte={parte}
          contexto={contexto}
          tipoDoBloco={bloco.tipo}
          origem={bloco.origem ?? null}
          secao={secao}
        />
      ))}
    </>
  );
}

function TituloDaSecao({ bloco }: { bloco: BlocoDoDocumentoData }): JSX.Element {
  return (
    <h3
      className="mb-2 mt-6 flex items-baseline gap-2 border-b-2 pb-1 text-[1.15em] font-semibold"
      style={{ borderColor: PALETA.navy, color: PALETA.navy }}
    >
      {bloco.numero !== null ? (
        <span
          className="px-1 font-mono text-[0.8em] text-white"
          style={{ background: PALETA.navy }}
        >
          {bloco.numero}
        </span>
      ) : null}
      <span className={bloco.caixa_alta ? 'uppercase tracking-[0.04em]' : undefined}>
        {bloco.titulo}
      </span>
    </h3>
  );
}

function Caixa({
  bloco,
  contexto,
  secao,
}: {
  bloco: BlocoDoDocumentoData;
  contexto: ContextoDasPartes;
  secao: string | null;
}) {
  return (
    <div
      className="my-3 border p-3 text-[0.95em]"
      style={{ borderColor: PALETA.linha2, background: PALETA.superficie }}
    >
      {bloco.titulo !== null ? (
        <p
          className="mb-1 border-l-2 pl-2 text-[0.85em] font-semibold uppercase tracking-[0.06em]"
          style={{ borderColor: PALETA.ciano, color: PALETA.navy2 }}
        >
          {bloco.titulo}
        </p>
      ) : null}
      <Partes bloco={bloco} contexto={contexto} secao={secao} />
    </div>
  );
}

/** A chave da seção do mapa do relatório de risco (`SECOES_DO_RISCO`). */
const SECAO_DO_MAPA = 'mapa';

/** A abertura, a tabela, o quadro e a grade; a tabela do mapa do risco edita os campos. */
function BlocoDeGrade({
  bloco,
  contexto,
  secao,
}: {
  bloco: BlocoDoDocumentoData;
  contexto: ContextoDasPartes;
  secao: string | null;
}): JSX.Element | null {
  if (bloco.grade === null) return null;
  if (bloco.tipo === 'tabela' && secao === SECAO_DO_MAPA) {
    return <MapaDoRisco grade={bloco.grade} contexto={contexto} origem={bloco.origem ?? null} />;
  }
  return (
    <div className="my-3">
      <GradeDoDocumento
        grade={bloco.grade}
        semFiletes={bloco.tipo === 'abertura'}
        origem={bloco.origem ?? null}
        secao={secao}
      />
    </div>
  );
}

export function BlocoDoDocumento({
  bloco,
  contexto,
  secao = null,
}: {
  bloco: BlocoDoDocumentoData;
  contexto: ContextoDasPartes;
  /** A chave da seção em que o bloco está (o mapa do risco tem campos editáveis). */
  secao?: string | null;
}): JSX.Element | null {
  switch (bloco.tipo) {
    case 'secao':
      return <TituloDaSecao bloco={bloco} />;
    case 'titulo':
      return (
        <h4
          className="mb-1 mt-4 border-b pb-0.5 text-[1em] font-semibold"
          style={{ borderColor: PALETA.linha, color: PALETA.navy }}
        >
          {bloco.titulo}
        </h4>
      );
    case 'subtitulo':
      return (
        <h4
          className="mb-1 mt-3 border-l-[3px] pl-2 text-[0.95em] font-semibold"
          style={{ borderColor: PALETA.ciano, color: PALETA.navy2 }}
        >
          {bloco.titulo}
        </h4>
      );
    case 'caixa':
      return <Caixa bloco={bloco} contexto={contexto} secao={secao} />;
    case 'paragrafo':
    case 'nota':
    case 'itens':
      return <Partes bloco={bloco} contexto={contexto} secao={secao} />;
    case 'quebra':
      return (
        <p
          className="my-6 border-t border-dashed pt-1 text-center text-[0.7em] uppercase tracking-[0.12em]"
          style={{ borderColor: PALETA.linha, color: PALETA.mudo }}
          aria-hidden="true"
        >
          {t.quebra}
        </p>
      );
    default:
      return <BlocoDeGrade bloco={bloco} contexto={contexto} secao={secao} />;
  }
}
